import {
  ChatMessageAttachmentType,
  SendWhatsappMessageMutationParams,
  useRestApiProvider,
  whatsappConversations$,
  WhatsappMessageModel,
  WhatsappTemplateModel,
} from "@jugl-web/rest-api";
import {
  ChatInput,
  ChatInputHandle,
  ChatInputParams,
} from "@web-src/modules/chats/components/ChatInput";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useRef, useMemo, useCallback, useEffect, useState } from "react";
import { isAPIError, useToast, useTranslations } from "@jugl-web/utils";
import { useUserGeneralProfile } from "@jugl-web/domain-resources/users/hooks/useUserGeneralProfile";
import { useSelector } from "react-redux";
import { selectUserId } from "@web-src/features/auth/authSlice";
import { AttachmentType } from "@web-src/modules/chats/types";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { WhatsappConversationModel } from "@jugl-web/rest-api/integrations/models/WhatsappConversation";
import { WhatsappTemplatesSidebar } from "../../../WhatsappTemplatesSidebar";
import { useGetSenderDetails } from "../../hooks/useGetSenderDetails";
import { useClientSupportPageWithSelectedConversation } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageWithSelectedConversation";
import { isWebMessage } from "../../../../utils/typeGuards";
import { useWhatsappAccount } from "../../../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { DisabledChatInput } from "../DisabledChatInput";
import { CRMInvalidUserPopup } from "../CRMInvalidUserPopup";

export const WhatsappChatInput: FC<{
  chatId: string;
  isChatExpired?: boolean;
  onMessageSent?: (message: WhatsappMessageModel) => void;
}> = ({ chatId, isChatExpired, onMessageSent }) => {
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const { toast } = useToast();
  const { reconnectWhatsappAccount, isRegisteringAccount, isConnected } =
    useWhatsappAccount();
  const {
    selectedConversation,
    replyMessage,
    setReplyMessage,
    incomingWhatsappMessages$,
    setIsSendingMessage,
  } = useClientSupportPageWithSelectedConversation();
  const { getSenderDetails } = useGetSenderDetails();
  const { senderName } = getSenderDetails(replyMessage);
  const meId = useSelector(selectUserId);
  const { profile: meProfile } = useMe();
  const [isInvalidUserPopupOpen, setIsInvalidUserPopupOpen] = useState(false);

  const { safeProfile: userSafeProfile } = useUserGeneralProfile({
    entityId,
    userId: replyMessage?.from,
    skip: !replyMessage || replyMessage.type !== "outgoing" || !!senderName,
  });

  const replyTitle = useMemo(() => {
    if (!replyMessage) return undefined;

    const name = senderName || userSafeProfile.displayName;
    const isYou = replyMessage.from === meId;

    return `${name}${
      isYou
        ? ` (${t({
            id: "common.you",
            defaultMessage: "You",
          })})`
        : ""
    }`;
  }, [senderName, userSafeProfile, replyMessage, meId, t]);

  const [sendMessage, { isLoading: isSendingMessage }] =
    integrationsApi.useSendWhatsappMessageMutation();
  const [uploadMedia, { isLoading: isUploadingMedia }] =
    integrationsApi.useUploadWhatsappMediaMutation();
  const chatInput$ = useRef<ChatInputHandle>(null);
  const [isTemplatesSidebarOpen, setIsTemplatesSidebarOpen] = useState(false);

  useEffect(() => {
    if (replyMessage) {
      chatInput$.current?.focus();
    }
  }, [replyMessage]);

  const handleSendMessage: ChatInputParams["onSubmit"] = async ({
    body,
    attachments,
  }) => {
    try {
      setIsSendingMessage(true);
      const data: SendWhatsappMessageMutationParams = {
        entityId,
        data: {
          conv_name: selectedConversation.name,
          to: selectedConversation.conv_id,
          type: "text",
          payload: {
            body,
          },
          context: replyMessage
            ? {
                id: replyMessage.msg_id,
                from: replyMessage.from,
              }
            : undefined,
        },
      };
      const attachment = attachments?.[0];
      if (attachment) {
        const { id: mediaId } = await uploadMedia({
          entityId,
          file: attachment.file,
        }).unwrap();
        switch (attachment.type) {
          case ChatMessageAttachmentType.image:
            data.data = {
              ...data.data,
              type: "image",
              payload: {
                id: mediaId,
                caption: body,
              },
            };
            break;
          case ChatMessageAttachmentType.audio:
          case ChatMessageAttachmentType.voice:
            data.data = {
              ...data.data,
              type: "audio",
              payload: {
                id: mediaId,
              },
            };
            break;
          case ChatMessageAttachmentType.video:
            data.data = {
              ...data.data,
              type: "video",
              payload: {
                id: mediaId,
                caption: body,
              },
            };
            break;
          default:
            data.data = {
              ...data.data,
              type: "document",
              payload: {
                id: mediaId,
                caption: body,
                filename: attachment.file.name,
              },
            };
            break;
        }
      }
      const message = await sendMessage(data).unwrap();
      const messageWithReply = {
        ...message,
        reply: replyMessage as WhatsappMessageModel,
        attended_by: meProfile?.displayName,
      };
      whatsappConversations$.next({
        type: "update",
        conversation: {
          ...(selectedConversation as WhatsappConversationModel),
          ...(messageWithReply as WhatsappMessageModel),
          attended_by: meProfile?.firstName || "",
        },
      });
      onMessageSent?.(messageWithReply);
      chatInput$.current?.clear();
      chatInput$.current?.focus();
      // Clear reply message after sending
      setReplyMessage(selectedConversation.conv_id, undefined);
    } catch (error) {
      if (isAPIError(error) && error.data.errors === "invalid_user_id") {
        setIsInvalidUserPopupOpen(true);
        return;
      }

      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleSendTemplate = async (template: WhatsappTemplateModel) => {
    try {
      const message = await sendMessage({
        entityId,
        data: {
          to: selectedConversation.conv_id,
          conv_name: selectedConversation.name,
          name: template.name,
        },
      }).unwrap();
      incomingWhatsappMessages$.next(message);
      onMessageSent?.(message);
    } catch (error) {
      // NOTE: pass
    }
  };

  const replyMessageText = useMemo(() => {
    if (
      !replyMessage ||
      !("type" in replyMessage.payload) ||
      isWebMessage(replyMessage)
    )
      return "";

    switch (replyMessage.payload.type) {
      case "text":
        return (
          (replyMessage.type !== "info" && replyMessage.payload.text?.body) ||
          ""
        );
      case "image":
        return (
          replyMessage.payload.image?.caption ||
          t({
            id: "client-support-page.reply-image",
            defaultMessage: "Image",
          })
        );
      case "video":
        return (
          replyMessage.payload.video?.caption ||
          t({
            id: "client-support-page.reply-video",
            defaultMessage: "Video",
          })
        );
      case "audio":
        return t({
          id: "client-support-page.reply-audio",
          defaultMessage: "Audio message",
        });
      case "document":
        return (
          replyMessage.payload.document?.filename ||
          t({
            id: "client-support-page.reply-document",
            defaultMessage: "Document",
          })
        );
      default:
        return t({
          id: "client-support-page.unsupported-message-format",
          defaultMessage: "Unsupported message format",
        });
    }
  }, [replyMessage, t]);

  const handleCancelReply = useCallback(() => {
    setReplyMessage(selectedConversation.conv_id, undefined);
  }, [selectedConversation.conv_id, setReplyMessage]);

  if (!isConnected) {
    return (
      <DisabledChatInput
        text={t({
          id: "client-support-page.whatsapp-acoount-is-not-connected-please-reconnect",
          defaultMessage:
            "⚠️ WhatsApp Account is not connected. Please reconnect to continue using WhatsApp CRM",
        })}
        buttonProps={{
          text: t({
            id: "common.reconnect",
            defaultMessage: "Reconnect",
          }),
          onClick: reconnectWhatsappAccount,
          isDisabled: isRegisteringAccount,
        }}
      />
    );
  }

  if (isChatExpired) {
    return (
      <>
        <WhatsappTemplatesSidebar
          isOpen={isTemplatesSidebarOpen}
          onRequestClose={() => setIsTemplatesSidebarOpen(false)}
          isLoading={isSendingMessage}
          onSelectTemplate={handleSendTemplate}
        />
        <DisabledChatInput
          text={t({
            id: "client-support-page.whatsapp-chat-is-expired",
            defaultMessage:
              "Chat Expired. You can send messages only by using WhatsApp Templates ℹ️",
          })}
          buttonProps={{
            text: t({
              id: "client-support-page.select-template",
              defaultMessage: "Select Template",
            }),
            onClick: () => setIsTemplatesSidebarOpen(true),
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className="px-8 pb-1.5 pt-[18px]">
        <ChatInput
          id={chatId}
          ref={chatInput$}
          onSubmit={handleSendMessage}
          isLoading={isSendingMessage || isUploadingMedia}
          toolbarActions={{
            emoji: {
              enabled: true,
            },
            attachments: {
              enabled: true,
              accept: {
                [AttachmentType.audio]: ".aac,.amr,.mp3,.m4a,.ogg",
                [AttachmentType.file]:
                  ".txt,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.pdf",
                [AttachmentType.image]: ".jpeg,.png",
                [AttachmentType.video]: ".3gp,.mp4",
              },
              maxSize: {
                [AttachmentType.audio]: 16,
                [AttachmentType.file]: 100,
                [AttachmentType.image]: 5,
                [AttachmentType.video]: 16,
              },
              isCaptionHidden: true,
            },
          }}
          actionMessageInfo={
            replyMessage
              ? {
                  type: "reply",
                  text: replyMessageText,
                  title: replyTitle,
                  onClose: handleCancelReply,
                }
              : undefined
          }
        />
      </div>
      <CRMInvalidUserPopup
        isOpen={isInvalidUserPopupOpen}
        onRequestClose={() => setIsInvalidUserPopupOpen(false)}
      />
    </>
  );
};
